// common/features/competition/wizard/transform.ts
import type {
  CloneCompetitionRequest,
  CreateCompetitionRequest,
  WizardFormValues,
} from './types'
import { isPlayoffsType } from './types'

export const buildCreateCompetitionPayload = (
  values: WizardFormValues
): CreateCompetitionRequest => {
  const { info, competitionType, structureType, joinType } = values

  // Simple Playoffs (no-source) skips the Join step entirely — it's always a
  // manually-managed bracket. Other formats must have joinType populated.
  const isSimplePlayoffsNoSource =
    competitionType === 'playoffs_simple' &&
    !values.cloneSource.sourceScheduleId
  const effectiveJoinType = isSimplePlayoffsNoSource ? 'manual' : joinType

  // Enhanced Playoffs always routes through /clone — it must never reach this
  // builder. Simple Playoffs and Tournament land here only when the user
  // skipped the optional source picker on Step 3.
  if (
    !info.sportId ||
    !info.sportType ||
    !info.name ||
    !info.startsAt ||
    !info.endsAt ||
    !competitionType ||
    !structureType ||
    !effectiveJoinType ||
    competitionType === 'playoffs_enhanced'
  ) {
    throw new Error('Cannot build create payload — wizard state is incomplete')
  }

  const abbreviation = info.name.replace(/\s+/g, '').slice(0, 8).toUpperCase()

  let divisions: CreateCompetitionRequest['divisions']
  let conferences: CreateCompetitionRequest['conferences']

  if (structureType === 'single') {
    // Single-division competitions always send one division named after the
    // competition itself — backend uses it as the implicit container.
    divisions = [{ name: info.name, order: 0 }]
  } else if (structureType === 'multiple') {
    divisions = values.divisions.map((d, idx) => ({
      name: d.name,
      order: idx,
    }))
  } else if (structureType === 'conferences') {
    conferences = values.conferences.map((c, cIdx) => ({
      name: c.name,
      order: cIdx,
      divisions: c.divisions.map((d, dIdx) => ({
        name: d.name,
        order: dIdx,
      })),
    }))
  }

  const email = info.email?.trim() || ''
  const website = info.website?.trim() || ''

  return {
    name: info.name,
    abbreviation,
    sport_id: info.sportId,
    sport_type: info.sportType,
    generic_sport_id: info.genericSportId ?? null,
    starts_at: info.startsAt,
    ends_at: info.endsAt,
    ...(email ? { email } : {}),
    ...(website ? { website } : {}),
    // competitionType is narrowed to season|playoffs_simple|tournament by the
    // playoffs_enhanced guard above.
    competition_type: competitionType as
      | 'season'
      | 'playoffs_simple'
      | 'tournament',
    is_tournament: values.isTournament,
    is_registration_on: effectiveJoinType === 'registration',
    registration_type: effectiveJoinType,
    type: structureType,
    ...(divisions ? { divisions } : {}),
    ...(conferences ? { conferences } : {}),
  }
}

/**
 * Builds the /clone payload when the user picked a clone-eligible competition
 * type (Playoffs or Tournament) in the new-create flow (Step 3). Reuses info
 * inputs from Step 2 + the source picked on Step 3, instead of the cloneSource
 * snapshot that the dedicated Clone flow uses end-to-end.
 */
export const buildClonePayloadFromNewFlow = (
  values: WizardFormValues
): CloneCompetitionRequest => {
  const { info, competitionType, cloneSource } = values
  const isCloneEligibleType =
    isPlayoffsType(competitionType) || competitionType === 'tournament'
  if (
    !cloneSource.sourceScheduleId ||
    !info.name ||
    !info.startsAt ||
    !info.endsAt ||
    !isCloneEligibleType
  ) {
    throw new Error(
      'Cannot build clone payload (new flow) — required fields are missing'
    )
  }
  const isTeamsCloneType =
    competitionType === 'playoffs_simple' || competitionType === 'tournament'
  // Only Tournament surfaces the Registration toggles on Step 3 — Playoffs
  // paths never see them, so the source flags stay forced to false there.
  const surfacesRegistrationToggles = competitionType === 'tournament'
  const isRegistration =
    surfacesRegistrationToggles && cloneSource.isRegistrationSchedule
  return {
    name: info.name,
    starts_at: info.startsAt,
    ends_at: info.endsAt,
    // isCloneEligibleType confirms competitionType is one of these three at
    // runtime; isPlayoffsType returns bool, not a type predicate, so TS can't
    // narrow it statically — the cast restates what the guard already enforces.
    competition_type: competitionType as
      | 'playoffs_simple'
      | 'playoffs_enhanced'
      | 'tournament',
    is_tournament:
      competitionType === 'playoffs_enhanced' ||
      competitionType === 'tournament',
    is_registration_schedule: isRegistration,
    clone_registration_settings: isRegistration
      ? cloneSource.cloneRegistrationSettings
      : false,
    // Registration competitions don't carry teams over.
    include_teams:
      isTeamsCloneType && !isRegistration ? cloneSource.cloneTeams : 'none',
  }
}

export const buildCloneCompetitionPayload = (
  values: WizardFormValues
): CloneCompetitionRequest => {
  const { cloneSource } = values
  if (
    !cloneSource.sourceScheduleId ||
    !cloneSource.name ||
    !cloneSource.startsAt ||
    !cloneSource.endsAt ||
    !cloneSource.competitionType
  ) {
    throw new Error('Cannot build clone payload — clone source is incomplete')
  }
  return {
    name: cloneSource.name,
    starts_at: cloneSource.startsAt,
    ends_at: cloneSource.endsAt,
    competition_type: cloneSource.competitionType,
    is_tournament: cloneSource.isTournament,
    is_registration_schedule: cloneSource.isRegistrationSchedule,
    clone_registration_settings: cloneSource.cloneRegistrationSettings,
    include_teams: cloneSource.cloneTeams,
  }
}
